import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createDevelopmentalRubric as createDevelopmentalRubricService,
  cloneDevelopmentalRubric as cloneDevelopmentalRubricService,
  updateDevelopmentalRubric as updateDevelopmentalRubricService,
  deleteDevelopmentalRubric as deleteDevelopmentalRubricService,
  archiveDevelopmentalRubric as archiveDevelopmentalRubricService,
  linkDevelopmentalRubric as linkDevelopmentalRubricService,
  unlinkDevelopmentalRubric as unlinkDevelopmentalRubricService,
  unlinkDevelopmentalRubricFromClass as unlinkDevelopmentalRubricFromClassService,
  getDevelopmentalRubricsSkipLimit as getDevelopmentalRubricsSkipLimitService,
  getDevelopmentalRubricById as getDevelopmentalRubricByIdService
} from '../../services/developmental-rubrics/developmental-rubrics.service.js';
import { validate } from '../../utils/index.js';
import { handlerErrorV2 } from '../../utils/errors.jsx';
import {
  updateDevelopmentalRubricForm,
  updateDevelopmentalRubricsList,
  updateDevelopmentalRubricsListMethod
} from '../../store/actions/developmental-rubrics.actions.js';
import isEmpty from 'lodash/isEmpty';
import { toggleAlertInfo, toggleAlertModule } from '../../store/actions/alert.actions';
import { useParams } from 'react-router-dom';

const useDevelopmentalRubrics = () => {
  const { studentId } = useParams();
  const subjects = useSelector((state) => state?.subject?.list || []);
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {});
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const getDevelopmentalRubricById = async (curriculumId, drId) => {
    try {
      const response = await getDevelopmentalRubricByIdService(curriculumId, drId);
      if (!response?.data && response?.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        const formData = response?.data || {};
        dispatch(updateDevelopmentalRubricForm(formData));
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const getDevelopmentalRubrics = useCallback(async (curriculumId, skip, rowsPerPage, filter) => {
    if (curriculumId) {
      setIsLoadingList(true);
      try {
        const filterData = {
          name: filter.name,
          yearLevel: filter.yearLevel,
          startDate: filter.periodStartDate,
          endDate: filter.periodEndDate,
          curriculumElements: filter.curriculumElements,
          subjectId: filter.subjectId,
          isLinked: filter.isLinked,
          isActived: filter.isActived,
          studentId
        };
        const response = await getDevelopmentalRubricsSkipLimitService(
          curriculumId,
          skip,
          rowsPerPage,
          filterData
        );
        if (!response?.data && response?.error) {
          enqueueSnackbar(response.error.message || 'An error ocurred', {
            variant: 'error'
          });
        } else {
          dispatch(updateDevelopmentalRubricsList(curriculumId, response?.data || { users: [], count: 0 }));
        }
      } catch (error) {
        const message = handlerErrorV2(error);
        enqueueSnackbar(message, { variant: 'error' });
      } finally {
        setIsLoadingList(false);
      }
    }
  }, []);

  const validateForm = (form, type = 'create') => {
    let fieldRules = {};

    if (type === 'create-0') {
      fieldRules = {
        name: 'required',
        yearLevel: 'required',
        elementId: 'required',
        startDate: `required|date|maxDate:${form.maxStartDate}`,
        endDate: `required|date|minDate:${form.minEndDate}`
      };
      if (subjects.length > 0) {
        fieldRules.subjectId = 'required';
      }
      if (form.curriculumElements?.elementId?.value) {
        const elementChildren = currentCurriculum?.elements?.children?.find(
          (item) => item._id === form.curriculumElements?.elementId?.value
        );
        if (elementChildren.elements.length > 0) {
          fieldRules.subElementId = 'required';
        }
      }
    }
    if (type === 'create-1') {
      if (isEmpty(form.lcs)) {
        return { error: true, lcs: 'Learning continuum is required' };
      }
    }

    if (type === 'create') {
      fieldRules = {
        name: 'required',
        yearLevel: 'required',
        elementId: 'required',
        lcs: 'required'
      };
    }
    if (type === 'basic-information') {
      fieldRules = {
        name: 'required',
        yearLevel: 'required',
        startDate: `required|date|maxDate:${form.maxStartDate}`,
        endDate: `required|date|minDate:${form.minEndDate}`
      };
    }
    if (type === 'subject') {
      if (subjects.length > 0) {
        fieldRules.subjectId = 'required';
      }
    }
    if (type === 'lcs' || type === 'granular-step') {
      fieldRules = {
        lcs: 'required'
      };
    }

    const errors = validate(fieldRules, {
      ...form,
      ...(form.curriculumElements || {})
    });
    if (!isEmpty(errors)) {
      return { error: true, ...errors };
    }

    return { error: false };
  };

  const formData = (form, type = 'create') => {
    if (type === 'basic-information') {
      return {
        name: form.name,
        yearLevel: form.yearLevel,
        startDate: form.startDate,
        endDate: form.endDate
      };
    }

    if (type === 'subject') {
      return {
        subjectId: form.subjectId
      };
    }

    if (type === 'lcs' || type === 'granular-step') {
      return {
        lcs: form.lcs
      };
    }

    if (type === 'growth-goals') {
      return {
        growthGoals: form.growthGoals
      };
    }
    if (type === 'worked-example') {
      return {
        workedExamples: form.workedExamples
      };
    }

    if (type === 'notes') {
      return {
        notes: form.notes
      };
    }

    return {
      name: form.name,
      yearLevel: form.yearLevel,
      startDate: form.startDate,
      endDate: form.endDate,

      subjectId: form.subjectId,
      curriculumElements: form.curriculumElements,

      lcs: form.lcs,

      growthGoals: form.growthGoals,
      workedExamples: form.workedExamples,
      notes: form.notes
    };
  };

  const createDevelopmentalRubric = async (curriculumId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result;
    }

    try {
      const data = formData(form);
      const response = await createDevelopmentalRubricService(curriculumId, data);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateDevelopmentalRubricsListMethod(curriculumId, 'POST', response.data));
        enqueueSnackbar('Developmental rubric created successfully', {
          variant: 'success'
        });
        dispatch(toggleAlertModule());

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const cloneDevelopmentalRubric = async (curriculumId, drId) => {
    try {
      const response = await cloneDevelopmentalRubricService(curriculumId, drId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateDevelopmentalRubricsListMethod(curriculumId, 'POST', response.data));
        enqueueSnackbar('Developmental rubric cloned successfully', {
          variant: 'success'
        });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const updateDevelopmentalRubric = async (curriculumId, drId, type, form = {}) => {
    if (!['basic-information', 'subject', 'notes'].includes(type)) {
      return { error: true, message: 'Update not allowed' };
    }

    const result = validateForm(form, type);
    if (result.error) {
      return result;
    }

    try {
      const data = formData(form, type);
      const response = await updateDevelopmentalRubricService(curriculumId, drId, type, data);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateDevelopmentalRubricsListMethod(curriculumId, 'PUT', response.data));
        dispatch(updateDevelopmentalRubricForm(response.data));
        getDevelopmentalRubricById(curriculumId, drId);
        enqueueSnackbar('Developmental rubric updated successfully', {
          variant: 'success'
        });
        dispatch(toggleAlertModule());

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const deleteDevelopmentalRubric = async (curriculumId, drId) => {
    setIsLoading(true);
    try {
      const response = await deleteDevelopmentalRubricService(curriculumId, drId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateDevelopmentalRubricsListMethod(curriculumId, 'DELETE', drId));
        enqueueSnackbar('Developmental rubric deleted successfully', {
          variant: 'success'
        });

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  const archiveDevelopmentalRubric = async (curriculumId, drId) => {
    setIsLoading(true);
    try {
      const response = await archiveDevelopmentalRubricService(curriculumId, drId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateDevelopmentalRubricsListMethod(curriculumId, 'DELETE', drId));
        enqueueSnackbar('Developmental rubric archived successfully', {
          variant: 'success'
        });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  const linkDevelopmentalRubric = async (curriculumId, drId) => {
    setIsLoading(true);
    try {
      dispatch(
        toggleAlertInfo(
          true,
          'Linking Developmental Rubric',
          'Please wait as this may take a few minutes',
          'loading'
        )
      );
      const response = await linkDevelopmentalRubricService(curriculumId, drId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateDevelopmentalRubricsListMethod(curriculumId, 'PUT', response.data));
        getDevelopmentalRubricById(curriculumId, drId);
        enqueueSnackbar('Developmental rubric linked successfully', {
          variant: 'success'
        });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
      dispatch(toggleAlertInfo(false));
    }
  };
  const unlinkDevelopmentalRubric = async (curriculumId, drId) => {
    setIsLoading(true);
    try {
      const response = await unlinkDevelopmentalRubricService(curriculumId, drId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateDevelopmentalRubricsListMethod(curriculumId, 'PUT', response.data));
        getDevelopmentalRubricById(curriculumId, drId);
        enqueueSnackbar('Developmental rubric unlinked successfully', {
          variant: 'success'
        });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  const unlinkDevelopmentalRubricFromClass = async (curriculumId, drId, classId) => {
    setIsLoading(true);
    try {
      const response = await unlinkDevelopmentalRubricFromClassService(curriculumId, drId, classId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateDevelopmentalRubricsListMethod(curriculumId, 'PUT', response.data));
        getDevelopmentalRubricById(curriculumId, drId);
        enqueueSnackbar('Class unlinked from developmental rubric successfully', {
          variant: 'success'
        });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoadingList,
    isLoading,
    validateFormDevelopmentalRubrics: validateForm,
    getDevelopmentalRubrics,
    getDevelopmentalRubricById,
    createDevelopmentalRubric,
    cloneDevelopmentalRubric,
    updateDevelopmentalRubric,
    deleteDevelopmentalRubric,
    archiveDevelopmentalRubric,
    linkDevelopmentalRubric,
    unlinkDevelopmentalRubric,
    unlinkDevelopmentalRubricFromClass
  };
};

export default useDevelopmentalRubrics;
